import { ApolloQueryResult } from '@apollo/client';
import { CSpinner } from '@coreui/react';
import { IAirportDetails, IFlightsInfo } from '@transferz/fe-models/src/transitTracking/transitTracking';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import Select from 'react-select';

import { ReactComponent as FlightLands } from '../../assets/svgs/flightLands.svg';
import { useSessionStorage } from '../../hooks/sessionStorage/useSessionStorage';
import { useBookingContext } from '../../hooks/useBookingContext';
import { useLocalize } from '../../hooks/useLocalize';
import { useQueryFlightsInfo } from '../../queries/transitTracking/hooks/useQueryFlightsInfo';
import { LocationSearchInputDesktop } from '../search/input/locationSearchInputDesktop/LocationSearchInputDesktop';
import { isAirport, SearchLocation } from '../search/input/utils';
import FlightCard from './FlightCard';
import styles from './FlightFinder.module.scss';

interface FlightFinderProps {
  destinationName: string;
  destinationId: string;
  outboundPickup: string;
  setOutboundFlightNumber: (flightNumber: string) => void;
  error: string;
  methods: UseFormMethods<any>;
}

interface IFlightOption {
  flightNumber: string;
  airlineName: string;
  originalDepartureTimeLocal: string;
  originalArrivalTimeLocal: string
}

const FlightFinder: React.FC<FlightFinderProps> = ({ destinationName, destinationId, outboundPickup, setOutboundFlightNumber, error, methods }) => {
  const { translate: t, locale } = useLocalize();
  const [loading, setLoading] = useState<boolean>(false);
  const { isMobile } = useBookingContext();
  const { partnerStylingOverrides } = window.tz_globalConfigs;
  const [storedSelectedFlight, setStoredSelectedFlight, removeStoredSelectedFlight] = useSessionStorage('storedSelectedFlight');
  const { refetch: getFlightsInfo } = useQueryFlightsInfo({
    variables: {
      params: {},
    },
    skip: true,
  });

  const [flights, setFlights] = useState<IFlightsInfo[] | undefined>(undefined);
  const [originLocation, setOriginLocation] = useState<SearchLocation | undefined>(storedSelectedFlight);
  const [formatedOriginLocation, setFormatedOriginLocation] = useState<string | undefined>();
  const [selectedAirline, setSelectedAirline] = useState<string | undefined>();
  const [selectedOption, setSelectedOption] = useState<IFlightOption | undefined>(undefined);
  const [airlinesArray, setAirlinesArray] = useState<string[]>([]);

  methods.register('outboundFlightNumber', { required: 'required' });

  const flightOptions = useMemo(() => {
    if (flights && flights?.length > 0) {
      const airlines = new Set<string>();
      flights?.forEach((flight) => {
        flight.flightDesignators.forEach((item) => {
          airlines.add(item.airlineName);
        });
      });

      setAirlinesArray(Array.from(airlines));

      let flightOptions: IFlightOption[] = [];
      const uniqueFlightNumbers = new Set<string>();

      flights?.forEach((flight) => {
        flight.flightDesignators.forEach((item) => {
          const flightNumber = `${item.airlineCode}${item.flightNumber}`;
          if (!uniqueFlightNumbers.has(flightNumber)) {
            uniqueFlightNumbers.add(flightNumber);
            if (selectedOption) {
              flightOptions = [{
                flightNumber: selectedOption.flightNumber,
                airlineName: selectedOption.airlineName,
                originalDepartureTimeLocal: selectedOption.originalDepartureTimeLocal,
                originalArrivalTimeLocal: selectedOption.originalArrivalTimeLocal,
              }];
            } else {
              if (!selectedAirline || item.airlineName === selectedAirline) {
                flightOptions.push({
                  flightNumber: flightNumber,
                  airlineName: item.airlineName,
                  originalDepartureTimeLocal: flight.originalDepartureTimeLocal,
                  originalArrivalTimeLocal: flight.originalArrivalTimeLocal,
                });
              }
            }
          }
        });
      });

      return flightOptions;
    }
  }, [flights, selectedAirline, selectedOption]);

  useEffect(() => {
    if (storedSelectedFlight) {
      handleSetLocation(storedSelectedFlight);
    }
  }, []);

  const handleSetLocation = useCallback(async (location: SearchLocation | undefined): Promise<void> => {
    const scrollPosition = window.scrollY;

    setLoading(true);
    setAirlinesArray([]);
    setSelectedAirline(undefined);
    setOriginLocation(location);
    location ? setStoredSelectedFlight(location) : removeStoredSelectedFlight();
    let flights: ApolloQueryResult<{
      flightsByHubIds: { flightDetails: IFlightsInfo[], originHubDetails: IAirportDetails }
    }> | undefined = undefined;

    try {
      if (location?.hubId && destinationId && outboundPickup) {
        flights = await getFlightsInfo({
          params: {
            originHubId: location?.hubId,
            destinationHubId: +destinationId,
            arrivalDateTime: `${outboundPickup}T12:00:00.000Z`,
          },
        });
      }

      setFlights(flights?.data.flightsByHubIds.flightDetails);
      setFormatedOriginLocation(`${flights?.data.flightsByHubIds.originHubDetails?.cityName} (${flights?.data.flightsByHubIds.originHubDetails?.iataCode})` || location?.subtitle);
      setLoading(false);
      setTimeout(() => window.scrollTo(0, scrollPosition), 1);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  }, [destinationId, outboundPickup, location, flightOptions]);

  return (
    <div className={`${styles.flightFinderWrapper} ${!isMobile ? styles.desktop : ''}`}>
      <h3>{t('flightDetails')}</h3>
      <div className={styles.flightDescription}>{t('flightDescription')}</div>
      {!selectedOption
        && <>
          <div className={styles.inputWrapper}>
            <div className="search" style={{ alignItems: 'start' }}>
              <label>{t('startSearching')}</label>
              <LocationSearchInputDesktop
              allowAddressSearch={false}
              locale={locale}
              setLocation={handleSetLocation}
              location={originLocation}
              dataTestId="origin-location"
              styles={styles}
              customIconType="TAKEOFF"
              asyncSelectStyles={{
                control: (base: any) => ({
                  ...base,
                  height: '50px',
                  gap: '8px',
                  border: `1px solid ${error && t('search_error_pickup_location') ? '#FC363B' : '#bfcbd4'} !important`,
                  borderRadius: '4px',
                  boxSizing: 'content-box',
                  cursor: 'pointer',
                  ...(error && t('search_error_pickup_location') ? { background: '#FDF0ED' } : {}),
                }),
                menuList: (base: any) => ({
                  ...base,
                  padding: '0',
                  maxHeight: '240px',
                }),
                placeholder: (base: any) => ({
                  ...base,
                  marginTop: '1px',
                }),
                noOptionsMessage: (base: any) => ({
                  ...base,
                  color: '#000',
                  fontVariantNumeric: 'lining-nums proportional-nums',
                  fontFeatureSettings: '\'clig\' off, \'liga\' off',
                  fontSize: '14px',
                  fontStyle: 'italic',
                  fontWeight: 500,
                  lineHeight: '20px',
                  textAlign: 'left',
                }),
              }}
              extraFilter={isAirport}
              editablePlaceholder="Where are you flying from?"
              valueLabel={'title'}
              />
              {error && <div className={styles.error}>{t('pleaseFindYourFlight')}</div>}
            </div>
            <div>
              <label className={styles.flyingTo}>{t('flyingTo')}</label>
              <div className={styles.destinationName}><FlightLands />{destinationName}</div>
            </div>
          </div>
          {loading && <div className={styles.spinner}><CSpinner color="primary" /></div>}
          {airlinesArray.length > 0 && <>
            <label className={styles.airlineLabel}>{t('selectYourAirline')}</label>
            <Select
            onChange={(selected) => {
              if (selected) {
                setSelectedAirline(selected.value);
              }
            }}
            options={airlinesArray.map(airline => ({ value: airline, label: airline })).sort((a, b) => a.label.localeCompare(b.label))}
            styles={
              {
                input: (base: any) => ({
                  ...base,
                  color: partnerStylingOverrides?.['whitelabel.forms.textColor'],

                }),
                placeholder: (base: any) => ({
                  ...base,
                  color: partnerStylingOverrides?.['whitelabel.forms.placeholderTextColor'] || '#808080',
                }),
                singleValue: (base: any) => ({
                  ...base,
                  color: partnerStylingOverrides?.['whitelabel.forms.textColor'],
                }),
                control: (base: any) => ({
                  ...base,
                  height: '50px',
                }),
              }
            }
            isMulti={false}
            placeholder={t('nameOrCode')}
            isDisabled={selectedOption !== undefined}
            id="airline-select"
            />
          </>}
        </>}
      {flights && flights?.length === 0 && <div className={styles.noFlights}>{t('noFlightsFound')}</div>}
      {
        (flightOptions ?? []).length > 0
        && <>
          {!selectedOption ? <>
            <h4>{t('selectYourFlight')}</h4>
            <div>{(flightOptions ?? []).length} {t('flightsFound')}</div>
          </> : <h4>{t('yourFlight')}</h4>}
          {(flightOptions ?? []).map(option => {
            return <div key={option.flightNumber}
              onClick={() => {
                setOutboundFlightNumber(option.flightNumber);
                methods.setValue('outboundFlightNumber', option.flightNumber);
                setSelectedOption(option);
              }
              }>
              <FlightCard
                airlineName={option.airlineName}
                flightNumber={option.flightNumber}
                origin={formatedOriginLocation || ''}
                destination={destinationName}
                departureTime={option.originalDepartureTimeLocal}
                arrivalTime={option.originalArrivalTimeLocal}
                isSelected={!!selectedOption?.flightNumber} />
            </div>;
          })}
          {selectedOption && <div className={styles.changeLink}>
            <span>{t('notYourFlight')} </span>
            <span onClick={() => {
              setSelectedOption(undefined);
              setSelectedAirline(undefined);
              setOutboundFlightNumber('');
              methods.setValue('outboundFlightNumber', undefined);
            }}>{t('changeItHere')}</span>
          </div>}
        </>
      }
      <div className={styles.divider}></div>
    </div >
  );
};

export default FlightFinder;
